import { fetchAdvisorsThunk } from '@/redux/asyncThunks/advisor'
import {
  connectAdvisorWebSocket,
  disconnectAdvisorWebSocket,
} from '@/redux/asyncThunks/websocketThunks'
import {
  connectNegotiationWebSocket,
  disconnectNegotiationWebSocket,
} from '@/redux/asyncThunks/negotiationWebsocket'
import { getRoomDetails, getRoomMembers } from '@/redux/asyncThunks/room'
import { fetchTeamsByRoomId } from '@/redux/asyncThunks/team'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import OGESidebar from '@/components/OGE/Sidebar/OGESidebar'
import OGESidebarContent from '@/components/OGE/Sidebar/OGESidebarContent'
import OGESidebarHeader from '@/components/OGE/Sidebar/OGESidebarHeader'
import Tabbar from '@/components/OGE/Tabbar/Tabbar'
import { SidebarProvider } from '@/components/ui/sidebar'
import ChatRoom from '@/components/Student/ChatRoom'
import StudentSidebarSkeleton from '@/components/Student/skeletons/StudentSidebarSkeleton'
import { UserRole } from '@/constants/room'
import {
  applyDefaultOpenRounds,
  buildScheduledReleaseRoundByEventId,
  groupEventsByRound,
} from '@/utils/groupEventsByRound'
import {
  connectDecisionWebSocket,
  disconnectDecisionWebSocket,
} from '@/redux/asyncThunks/studentDecisionWebsocket'
import {
  connectWaitingRoomWebSocket,
  disconnectWaitingRoomWebSocket,
} from '@/redux/asyncThunks/websocketThunks'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { GameRoomStatus, RoleChoice, Status } from '@/redux/types/room'
import { useGamePhase } from '@/hooks/useGamePhase'
import { usePhaseNavigation } from '@/hooks/usePhaseNavigation'
import GamePhaseInfo from '@/components/Game/GamePhaseInfo'
import GamePhaseStatusFetcher from '@/components/Game/GamePhaseStatusFetcher'
import {
  connectTimerWebSocket,
  disconnectTimerWebSocket,
} from '@/redux/asyncThunks/timerWebsocket'
import { fetchTimerStatusThunk } from '@/redux/asyncThunks/timerConfiguration'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  setActiveAdvisor,
  setActiveProfessor,
  clearMessages,
  setActiveTab,
  setInput,
  setLoadingAdvisorMsgs,
  setAdvisorDraft,
  clearAllNegotiationDrafts,
} from '@/redux/slices/chatSlice'
import { getAccessToken } from '@/utils/authUtils'
import { selectRoomPlayers } from '@/redux/selectors/roomSelector'
import RoomNotFound from '@/components/PageNotFound/RoomNotFound'
import {
  ActivityState,
  PhaseStatus,
} from '@/redux/slices/timerConfigurationSlice'
import {
  clearAllNegotiations,
  setIsLoading,
} from '@/redux/slices/negotiationWebsocketSlice'
import { clearMessages as clearTeamChatMessages } from '@/redux/slices/teamChatSlice'
import { ActiveTab, isCharterTab } from '@/constants/chat'
import {
  fetchCharterSession,
  fetchMyProfile,
  fetchMyTeamSummaries,
  fetchPrepHistory,
} from '@/features/charter/redux/asyncThunks/charterThunks'
import {
  connectCharterPrepWS,
  disconnectCharterPrepWS,
  connectCharterGroupWS,
  disconnectCharterGroupWS,
} from '@/features/charter/redux/asyncThunks/charterWsThunks'
import { shallowEqual } from 'react-redux'
import { fetchTeamChatbotsByTeam } from '@/redux/asyncThunks/teamChatbot'
import { getTeamNameById } from '@/utils/teamUtils'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import {
  connectTeamChatWebSocket,
  disconnectTeamChatWebSocket,
} from '@/redux/asyncThunks/teamChat'
import {
  connectAIPlayerWebSocket,
  disconnectAIPlayerWebSocket,
  loadAIPlayerThread,
} from '@/redux/asyncThunks/aiPlayerChat'
import { fetchTeamDummyAssignmentThunk } from '@/redux/asyncThunks/dummyBot'
import { setTeamAssignment } from '@/redux/slices/aiPlayerChatSlice'
import { clearTeamDecisionState } from '@/redux/slices/teamDecisionSlice'
import TeamOrientationInfoDialog from '@/components/Student/TeamOrientationInfoDialog'
import { Button } from '@/components/ui/button'
import { Info } from 'lucide-react'
import { markTeamOrientationShownAPI } from '@/api/room'

const PlayerGame = () => {
  const params = useParams()
  const dispatch = useAppDispatch()
  const roomId = params.roomId as string
  const roomPlayers = useSelector(selectRoomPlayers)
  const { teams } = useAppSelector((state) => state.team)

  const jwtToken = getAccessToken() || ''
  const activeTab = useAppSelector((state) => state.chat.activeTab)
  const teamDummyAssignment = useAppSelector(
    (state) => state.aiPlayerChat.teamAssignment
  )

  const negotiations = useAppSelector(
    (state) => state.negotiationWS.negotiations
  )

  const [searchParams] = useSearchParams()
  const negotiationId = UserRole.Professor
    ? searchParams.get('negotiationId') || ''
    : undefined

  const playerId = searchParams.get('playerId') || undefined
  const teamIdFromURL = searchParams.get('teamId') || undefined
  const inviteCode = searchParams.get('inviteCode') || ''
  const panelFromURL = searchParams.get('panel') || undefined

  const { currentRoom, roomMembers, isLoading, error } = useAppSelector(
    (state) => state.room,
    shallowEqual
  )
  const timerConfig = useAppSelector(
    (state) => state.timerConfiguration.config,
    shallowEqual
  )

  const { waitingRoomStatus, gameEnded } = useSelector(
    (state: RootState) => state.waitingRoomWS,
    shallowEqual
  )

  const { user, isSimulationMode, actualUser } = useEffectiveUser()

  const charterSession = useAppSelector((state) => state.charter.session)
  const prepStatus = useAppSelector((state) => state.charterPrepWS.status)
  const charterEnabled = currentRoom?.charter_enabled === true
  const isStudent = user?.role === UserRole.Student

  const [openEvents, setOpenEvents] = useState<{ [key: number]: boolean }>({})
  const [openRounds, setOpenRounds] = useState<Record<number, boolean>>({})
  const [isTeamOrientationOpen, setIsTeamOrientationOpen] = useState<boolean>(false)
  const lastAutoOpenedSessionRef = useRef<string | null>(null)
  const isMarkingOrientationShownRef = useRef<boolean>(false)

  const {
    currentPhase,
    isRandRPhase,
    isAdvisorPhase,
    isNegotiationPhase,
    isDecisionPhase,
    phaseId,
    roundStatus,
    totalRounds,
    currentRound,
    roundNumber,
    manualMode,
    advisorPhase,
    negotiationPhase,
    decisionPhase,
    roundId,
  } = useGamePhase()

  const isGameEnded = useMemo(
    () =>
      !!(
        gameEnded ||
        currentRoom?.status === GameRoomStatus.COMPLETED ||
        waitingRoomStatus === GameRoomStatus.COMPLETED
      ),
    [gameEnded, currentRoom?.status, waitingRoomStatus]
  )

  const isProfessorViewingPlayer = useMemo(
    () => user?.role === UserRole.Professor && !!playerId,
    [user?.role, playerId]
  )

  const isProfessorViewingTeam = useMemo(
    () =>
      user?.role === UserRole.Professor && !!teamIdFromURL && !playerId,
    [user?.role, teamIdFromURL, playerId]
  )

  const sidebarInitialPanel = useMemo(() => {
    if (user?.role !== UserRole.Professor) return 'game' as const
    if (panelFromURL === 'chat') return 'chat' as const
    if (playerId || teamIdFromURL || negotiationId) {
      return 'chat' as const
    }
    return 'game' as const
  }, [user?.role, panelFromURL, playerId, teamIdFromURL, negotiationId])

  const [sidebarPanel, setSidebarPanel] = useState<'game' | 'chat'>(
    sidebarInitialPanel
  )

  useEffect(() => {
    setSidebarPanel(sidebarInitialPanel)
  }, [sidebarInitialPanel])

  const activeNegotiations = useMemo(
    () =>
      negotiations.filter((negotiation) => {
        const allMembersAcceptedOrOwner = negotiation.members?.every(
          (member) =>
            member.status === Status.Accepted || member.status === Status.Owner
        )
        return allMembersAcceptedOrOwner
      }),
    [negotiations]
  )

  const isGameStarted = currentRound > 0

  usePhaseNavigation({
    dispatch,
    setSidebarPanel,
    userRole: user?.role,
    manualMode,
    isGameStarted,
    phaseId: phaseId ?? null,
    currentRound,
    currentPhase,
    isRandRPhase,
    isAdvisorPhase,
    isNegotiationPhase,
    isDecisionPhase,
    teamDummyAssignment,
    activeNegotiations,
  })

  const isWhiteCell = useMemo(
    () => user?.role === UserRole.Professor,
    [user?.role]
  )

  const currentNegotiation = useMemo(
    () =>
      negotiations.find(
        (negotiation) => negotiation.negotiationId.toString() == activeTab
      ),
    [negotiations, activeTab]
  )

  const isCurrentNegotiationTerminated = useMemo(
    () =>
      currentNegotiation?.end_time !== null &&
      currentNegotiation?.end_time !== undefined,
    [currentNegotiation?.end_time]
  )

  const showAdvisors = useMemo(
    () =>
      roundStatus !== ActivityState.Completed &&
      !isGameEnded &&
      !isProfessorViewingPlayer &&
      (roundStatus === ActivityState.Paused ||
        isWhiteCell ||
        isAdvisorPhase ||
        isRandRPhase),
    [
      roundStatus,
      isGameEnded,
      isProfessorViewingPlayer,
      isWhiteCell,
      isAdvisorPhase,
      isRandRPhase,
    ]
  )

  const showNegotiationPill = useMemo(() => {
    if (isWhiteCell) {
      return true
    }

    if (activeTab === ActiveTab.TeamRoom) {
      return (
        !gameEnded &&
        roundStatus !== ActivityState.Completed &&
        roundStatus !== ActivityState.Paused
      )
    }

    return (
      !gameEnded &&
      roundStatus !== ActivityState.Completed &&
      roundStatus !== ActivityState.Paused &&
      !isCurrentNegotiationTerminated &&
      (manualMode ||
        isWhiteCell ||
        (activeTab !== ActiveTab.Advisor && isNegotiationPhase))
    )
  }, [
    gameEnded,
    roundStatus,
    isCurrentNegotiationTerminated,
    manualMode,
    isWhiteCell,
    activeTab,
    isNegotiationPhase,
  ])

  const showAdvisorPill = useMemo(() => {
    if (isWhiteCell) {
      return true
    }

    return (
      !gameEnded &&
      roundStatus !== ActivityState.Completed &&
      (roundStatus === ActivityState.Paused ||
        manualMode ||
        (activeTab === ActiveTab.Advisor &&
          !manualMode &&
          (isRandRPhase || isAdvisorPhase)))
    )
  }, [
    gameEnded,
    roundStatus,
    manualMode,
    activeTab,
    isRandRPhase,
    isAdvisorPhase,
    isWhiteCell,
  ])

  const showNegotiation = useMemo(
    () =>
      isGameEnded
        ? false
        : isWhiteCell
          ? true
          : isNegotiationPhase || isDecisionPhase,
    [isGameEnded, isWhiteCell, isNegotiationPhase, isDecisionPhase]
  )

  const showDecisions = useMemo(
    () => !manualMode && activeTab === ActiveTab.Decision,
    [manualMode, activeTab]
  )

  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)

  const playerTeam = useMemo(() => {
    if (isProfessorViewingTeam && teamIdFromURL) {
      return parseInt(teamIdFromURL)
    }

    return roomPlayers?.find((player) =>
      isProfessorViewingPlayer
        ? String(player.member_id) === playerId
        : player.member_email === user?.email
    )?.teamId
  }, [
    roomPlayers,
    isProfessorViewingPlayer,
    isProfessorViewingTeam,
    teamIdFromURL,
    playerId,
    user?.email,
  ])

  const currentTeam = useMemo(
    () => teams.find((team) => team.id === playerTeam) ?? null,
    [teams, playerTeam]
  )

  useEffect(() => {
    if (roomId) {
      dispatch(getRoomDetails(roomId)).finally(() => {
        if (isInitialLoading) {
          setIsInitialLoading(false)
        }
      })
      dispatch(setAdvisorDraft(''))
      dispatch(clearAllNegotiationDrafts())
      dispatch(fetchTeamsByRoomId(roomId))
    }
  }, [dispatch, roomId, isInitialLoading])

  useEffect(() => {
    if (waitingRoomStatus === GameRoomStatus.NOT_STARTED) {
      dispatch(clearMessages())
      dispatch(clearTeamChatMessages())
      // When charter is enabled the active tab is owned by the charter
      // default-tab effect below; don't force the Advisor tab here.
      if (!charterEnabled) {
        dispatch(setActiveTab(ActiveTab.Advisor))
      }
      dispatch(setActiveAdvisor([]))
      dispatch(setActiveProfessor(false))
      dispatch(setInput(''))
      dispatch(setAdvisorDraft(''))
      dispatch(clearAllNegotiationDrafts())
      dispatch(clearAllNegotiations())
    }
  }, [waitingRoomStatus, dispatch, charterEnabled])

  useEffect(() => {
    if (!user || !jwtToken) return

    const shouldFetchAdvisors = !manualMode
      ? isRandRPhase || isAdvisorPhase
      : true
    if (shouldFetchAdvisors) {
      const params =
        !manualMode && isRandRPhase
          ? { roomId, playerId: user.email }
          : { roomId }
      dispatch(fetchAdvisorsThunk(params))
    }

    dispatch(getRoomMembers(roomId))
  }, [dispatch, roomId, user?.id, playerTeam, manualMode, isRandRPhase, isAdvisorPhase])

  useEffect(() => {
    if (user?.role !== UserRole.Student || !playerTeam) {
      dispatch(setTeamAssignment(null))
      return
    }

    dispatch(fetchTeamChatbotsByTeam(playerTeam))
    dispatch(fetchTeamDummyAssignmentThunk({ roomId, teamId: playerTeam }))
      .unwrap()
      .then((assignment) => {
        dispatch(
          setTeamAssignment(
            assignment
              ? {
                  botId: assignment.bot,
                  botName: assignment.bot_name,
                  teamId: assignment.team,
                  teamName: assignment.team_name,
                }
              : null
          )
        )
      })
      .catch(() => dispatch(setTeamAssignment(null)))
  }, [dispatch, roomId, user?.role, playerTeam])

  // ── Charter integration ──────────────────────────────────────────────────
  // Fetch charter REST state (session, own profile, team summaries, prep
  // history) so the charter tabs + their sidebars have data.
  useEffect(() => {
    if (!isStudent || !charterEnabled || !roomId) return
    dispatch(fetchCharterSession({ roomId }))
    dispatch(fetchMyProfile({ roomId }))
    dispatch(fetchMyTeamSummaries({ roomId }))
    dispatch(fetchPrepHistory({ roomId }))
  }, [dispatch, isStudent, charterEnabled, roomId])

  // Connect the charter WebSockets (prep always; group once Phase 2 exists).
  const charterTeamId = charterSession?.team_id
  const charterPhase = charterSession?.phase
  const charterStatus = charterSession?.status
  useEffect(() => {
    if (!isStudent || !charterEnabled || !jwtToken || !inviteCode) return
    if (!charterTeamId) return
    const teamIdStr = String(charterTeamId)
    dispatch(
      connectCharterPrepWS({ jwtToken, roomId, inviteCode, teamId: teamIdStr })
    )
    const groupReady =
      charterPhase === 'phase2_group' ||
      charterPhase === 'ratified' ||
      charterStatus === 'locked'
    if (groupReady) {
      dispatch(
        connectCharterGroupWS({
          jwtToken,
          roomId,
          inviteCode,
          teamId: teamIdStr,
          monitor: false,
        })
      )
    }
    return () => {
      dispatch(disconnectCharterPrepWS())
      if (groupReady) {
        dispatch(disconnectCharterGroupWS())
      }
    }
  }, [
    dispatch,
    isStudent,
    charterEnabled,
    jwtToken,
    inviteCode,
    roomId,
    charterTeamId,
    charterPhase,
    charterStatus,
  ])

  // Pre-game: default the active tab to the relevant charter channel (once),
  // and surface the chat panel.
  const charterDefaultAppliedRef = useRef(false)
  useEffect(() => {
    if (!isStudent || !charterEnabled || !charterSession) return
    if (isGameStarted) return
    // Only surface the charter UI once the professor has actually started Round 0.
    if (charterSession.round_status !== 'in_progress') return
    if (charterDefaultAppliedRef.current) return
    charterDefaultAppliedRef.current = true
    setSidebarPanel('chat')
    if (
      charterPhase === 'phase2_group' ||
      charterPhase === 'ratified' ||
      charterStatus === 'locked'
    ) {
      dispatch(setActiveTab(ActiveTab.CharterGroup))
    } else {
      dispatch(setActiveTab(ActiveTab.CharterPrep))
    }
  }, [
    dispatch,
    isStudent,
    charterEnabled,
    charterSession,
    charterSession?.round_status,
    charterPhase,
    charterStatus,
    isGameStarted,
  ])

  // Pre-game: when Phase 1 prep unlocks Phase 2 via WebSocket (self-driven),
  // move to the Group tab.
  useEffect(() => {
    if (!isStudent || !charterEnabled || isGameStarted) return
    if (prepStatus === 'phase2_ready' || prepStatus === 'phase2_unlocked') {
      // The prep WS only flips prepStatus — it does NOT update the REST session
      // phase. Refetch so charterSession.phase becomes 'phase2_group', which
      // makes the Group tab render and triggers the group-WS connection (and
      // thus the moderator opening) without requiring a manual page reload.
      if (roomId) dispatch(fetchCharterSession({ roomId }))
      setSidebarPanel('chat')
      dispatch(setActiveTab(ActiveTab.CharterGroup))
    }
  }, [dispatch, isStudent, charterEnabled, isGameStarted, prepStatus, roomId])

  // Pre-game: when the professor drives Phase 2 (advance action), reactively
  // switch the player to the Group Moderator tab. This is NOT one-shot so it
  // fires every time charterPhase changes (unlike charterDefaultAppliedRef).
  useEffect(() => {
    if (!isStudent || !charterEnabled || isGameStarted) return
    if (charterSession?.round_status !== 'in_progress') return
    if (
      charterPhase === 'phase2_group' ||
      charterPhase === 'ratified' ||
      charterStatus === 'locked'
    ) {
      setSidebarPanel('chat')
      dispatch(setActiveTab(ActiveTab.CharterGroup))
    }
  }, [
    dispatch,
    isStudent,
    charterEnabled,
    isGameStarted,
    charterSession?.round_status,
    charterPhase,
    charterStatus,
  ])

  // Reset the one-shot ref when the professor resets the round so that
  // Phase 1 is re-surfaced correctly after a reset → start cycle.
  useEffect(() => {
    if (!isStudent || !charterEnabled) return
    if (charterSession?.round_status === 'not_started') {
      charterDefaultAppliedRef.current = false
    }
  }, [isStudent, charterEnabled, charterSession?.round_status])

  // On game start, release a lingering charter tab once so the normal game
  // phase navigation (Advisor / Decision / events) takes over. The charter
  // tabs remain available as read-only reference if the player re-selects them.
  const charterReleasedRef = useRef(false)
  useEffect(() => {
    if (!isStudent) return
    if (!isGameStarted) {
      charterReleasedRef.current = false
      return
    }
    if (charterReleasedRef.current) return
    charterReleasedRef.current = true
    if (isCharterTab(activeTab)) {
      dispatch(setActiveTab(ActiveTab.Advisor))
    }
  }, [dispatch, isStudent, isGameStarted, activeTab])

  // When the game goes live in place (player already mounted on a charter tab),
  // the timer-derived `isGameStarted` can lag/miss the transition. Key off the
  // authoritative room status (REST + waiting-room WS) instead: on the first
  // transition to live, force a fresh timer-status + room-details fetch (mirrors
  // what a fresh mount does) and release any lingering charter tab.
  const gameIsLive =
    currentRoom?.status === GameRoomStatus.IN_PROGRESS ||
    waitingRoomStatus === GameRoomStatus.IN_PROGRESS
  const liveResyncRef = useRef(false)
  useEffect(() => {
    if (!isStudent) return
    if (!gameIsLive) {
      liveResyncRef.current = false
      return
    }
    if (liveResyncRef.current) return
    liveResyncRef.current = true
    dispatch(fetchTimerStatusThunk(roomId))
    dispatch(getRoomDetails(roomId))
    if (isCharterTab(activeTab)) {
      dispatch(setActiveTab(ActiveTab.Advisor))
    }
  }, [dispatch, isStudent, gameIsLive, roomId, activeTab])

  // Lifecycle sockets (timer + waiting room) are NOT phase-dependent. Keep them
  // in their own effect with a minimal dependency array so the phase-driven
  // socket effect below can re-run on every phase/round change WITHOUT tearing
  // the timer socket down mid-transition. (When a charter player sits in this
  // screen across the pre-game → start transition, the timer status 0→1 change
  // would otherwise churn the very socket delivering it, leaving the game stuck.)
  useEffect(() => {
    if (!user || !jwtToken) return
    if (inviteCode) {
      dispatch(connectWaitingRoomWebSocket({ jwtToken, roomId, inviteCode }))
    }
    dispatch(connectTimerWebSocket({ jwtToken, roomId }))
    return () => {
      dispatch(disconnectWaitingRoomWebSocket())
      dispatch(disconnectTimerWebSocket())
    }
  }, [dispatch, user?.id, jwtToken, roomId, inviteCode])

  useEffect(() => {
    if (!user || !jwtToken) return

    const getPhaseId = (phase: PhaseStatus | null) =>
      manualMode ? phase?.id.toString() : phaseId?.toString()
    const getTeamChatPhaseId = () => {
      if (manualMode) {
        if (isDecisionPhase) return decisionPhase?.id?.toString()
        if (isNegotiationPhase) return negotiationPhase?.id?.toString()
      }
      return phaseId?.toString()
    }

    const impersonatePlayerId =
      isSimulationMode &&
      user?.role === UserRole.Student &&
      user?.id &&
      user.id !== actualUser?.id
        ? String(user.id)
        : undefined

    const professorViewingTeam =
      isProfessorViewingTeam && teamIdFromURL
        ? teams.find((team) => team.id === parseInt(teamIdFromURL, 10))
        : null

    if (currentTeam || professorViewingTeam) {
      const teamIdToUse = professorViewingTeam
        ? String(professorViewingTeam.id)
        : String(currentTeam!.id)
      dispatch(
        connectTeamChatWebSocket({
          jwtToken,
          roomId,
          teamId: teamIdToUse,
          phaseId: getTeamChatPhaseId(),
          impersonatePlayerId,
        })
      )
    }

    if (
      isNegotiationPhase &&
      playerTeam &&
      user?.role === UserRole.Student &&
      !!teamDummyAssignment
    ) {
      dispatch(
        connectAIPlayerWebSocket({
          jwtToken,
          roomId,
          teamId: String(playerTeam),
        })
      )
      dispatch(loadAIPlayerThread({ page: 1 }))
    } else {
      dispatch(disconnectAIPlayerWebSocket())
    }

    if ((isNegotiationPhase || isDecisionPhase) && !isWhiteCell) {
      dispatch(setIsLoading(true))
      dispatch(
        connectNegotiationWebSocket({
          jwtToken,
          roomId,
          negotiationId,
          phaseId: getPhaseId(negotiationPhase),
          roundId: roundId?.toString(),
          impersonatePlayerId,
        })
      )
    } else if (isWhiteCell && !(isProfessorViewingTeam && !negotiationId)) {
      dispatch(setIsLoading(true))
      dispatch(
        connectNegotiationWebSocket({
          jwtToken,
          roomId,
          negotiationId,
          impersonatePlayerId,
        })
      )
    }

    if (
      inviteCode &&
      !isProfessorViewingPlayer &&
      (isNegotiationPhase || isDecisionPhase || isAdvisorPhase || isRandRPhase)
    ) {
      dispatch(setActiveProfessor(false))
      dispatch(setActiveAdvisor([]))
      dispatch(setInput(''))
      dispatch(setLoadingAdvisorMsgs(true))
      dispatch(
        connectAdvisorWebSocket({
          jwtToken,
          inviteCode,
          impersonatePlayerId,
        })
      )
    } else if (inviteCode && isProfessorViewingPlayer) {
      dispatch(setLoadingAdvisorMsgs(true))
      dispatch(
        connectAdvisorWebSocket({
          jwtToken,
          inviteCode,
          studentId: playerId,
        })
      )
    }

    if (
      user?.role === UserRole.Student &&
      (isNegotiationPhase || isDecisionPhase || isAdvisorPhase || isRandRPhase)
    ) {
      dispatch(
        connectDecisionWebSocket({
          jwtToken,
          roomId,
          impersonatePlayerId,
        })
      )
    }

    return () => {
      dispatch(disconnectNegotiationWebSocket())
      dispatch(disconnectAdvisorWebSocket())
      dispatch(disconnectDecisionWebSocket())
      dispatch(disconnectTeamChatWebSocket())
      dispatch(disconnectAIPlayerWebSocket())
      dispatch(clearTeamDecisionState())
    }
  }, [
    dispatch,
    phaseId,
    advisorPhase?.id,
    negotiationPhase?.id,
    decisionPhase?.id,
    roomId,
    playerTeam,
    teamIdFromURL,
    isProfessorViewingTeam,
    teams,
    currentTeam,
    isDecisionPhase,
    isNegotiationPhase,
    isAdvisorPhase,
    isRandRPhase,
    isProfessorViewingPlayer,
    user?.id,
    user?.role,
    inviteCode,
    jwtToken,
    manualMode,
    roundId,
    isWhiteCell,
    negotiationId,
    actualUser?.id,
    isSimulationMode,
    playerId,
    teamDummyAssignment,
  ])

  useEffect(() => {
    dispatch(clearTeamDecisionState())
  }, [dispatch, roundId])

  useEffect(() => {
    if (roundStatus === ActivityState.Completed || gameEnded) {
      dispatch(clearTeamDecisionState())
    }
  }, [dispatch, roundStatus, gameEnded])


  useEffect(() => {
    if (!currentRoom?.events?.length) return
    const scheduledReleaseByEventId = isWhiteCell
      ? buildScheduledReleaseRoundByEventId(timerConfig?.roundConfigurations)
      : undefined
    const grouped = groupEventsByRound(currentRoom.events, {
      includeUnreleased: isWhiteCell,
      scheduledReleaseByEventId,
    })
    setOpenRounds((prev) => applyDefaultOpenRounds(prev, grouped, currentRound))
  }, [currentRoom?.events, currentRound, isWhiteCell, timerConfig?.roundConfigurations])

  const onRoundToggle = useCallback((roundKey: number, isOpen: boolean) => {
    setOpenRounds((prev) => ({
      ...prev,
      [roundKey]: isOpen,
    }))
  }, [])

  const viewedPlayer = useMemo(
    () => roomPlayers?.find((player) => String(player.member_id) === playerId),
    [roomPlayers, playerId]
  )

  const teamName = useMemo(
    () => (playerTeam ? getTeamNameById(teams, playerTeam) : undefined),
    [playerTeam, teams]
  )

  const headerTitle = useMemo(
    () => {
      if (isProfessorViewingPlayer) {
        return `Viewing Player: ${viewedPlayer?.member_name || playerId}`
      }
      if (isProfessorViewingTeam && teamName) {
        return `Team: ${teamName}`
      }
      return user?.name || 'Player Name'
    },
    [
      isProfessorViewingPlayer,
      isProfessorViewingTeam,
      viewedPlayer?.member_name,
      playerId,
      teamName,
      user?.name,
    ]
  )

  const teamMembers = useMemo(() => {
    if (!playerTeam) return []
    return roomMembers.filter(
      (member) =>
        member.teamId === playerTeam &&
        member.role === RoleChoice.PLAYER &&
        member.invitation_status === Status.Accepted
    )
  }, [playerTeam, roomMembers])

  const isRound1OrientationStart = useMemo(() => {
    const isStartedState =
      currentRoom?.status === GameRoomStatus.IN_PROGRESS ||
      waitingRoomStatus === GameRoomStatus.IN_PROGRESS

    return isStartedState && roundNumber === 1
  }, [currentRoom?.status, waitingRoomStatus, roundNumber])

  const handleOrientationAcknowledge = useCallback(() => {
    setIsTeamOrientationOpen(false)
  }, [])

  const markTeamOrientationShown = useCallback(async () => {
    if (!roomId || isMarkingOrientationShownRef.current) return

    isMarkingOrientationShownRef.current = true
    try {
      await markTeamOrientationShownAPI(roomId)
      await dispatch(getRoomDetails(roomId))
    } catch (error) {
      console.error('Failed to mark team orientation as shown:', error)
    } finally {
      isMarkingOrientationShownRef.current = false
    }
  }, [dispatch, roomId])

  useEffect(() => {
    if (user?.role !== UserRole.Student) return
    if (!playerTeam || !currentTeam) return
    if (!isRound1OrientationStart) return
    if (!currentRoom?.current_session_uuid) return
    if (currentRoom.team_orientation_shown) return
    if (lastAutoOpenedSessionRef.current === currentRoom.current_session_uuid) return

    setIsTeamOrientationOpen(true)
    lastAutoOpenedSessionRef.current = currentRoom.current_session_uuid
    void markTeamOrientationShown()
  }, [
    user?.role,
    playerTeam,
    currentTeam,
    currentRoom?.current_session_uuid,
    currentRoom?.team_orientation_shown,
    isRound1OrientationStart,
    markTeamOrientationShown,
  ])

  const onEventToggle = useCallback((eventId: number, isOpen: boolean) => {
    setOpenEvents((prev) => ({
      ...prev,
      [eventId]: isOpen,
    }))
  }, [])

  if (isInitialLoading && isLoading) {
    return <StudentSidebarSkeleton />
  }

  if (error) {
    return (
      <div className='flex items-center justify-center w-full h-full text-center'>
        <RoomNotFound message='Unable to fetch the room.' />
      </div>
    )
  }

  return (
    <SidebarProvider className='h-screen'>
      <GamePhaseStatusFetcher roomId={roomId} pollInterval={3000} />
      <OGESidebar
        initialPanel={sidebarInitialPanel}
        activePanel={sidebarPanel}
        onActivePanelChange={setSidebarPanel}
        header={
          <OGESidebarHeader
            userName={headerTitle}
            avatarSrc={user?.profile_picture || ''}
            subText={teamName ? `Team: ${teamName}` : undefined}
            subTextAction={
              user?.role === UserRole.Student && teamName ? (
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='h-5 w-5 text-gray-500 hover:text-gray-700'
                  aria-label='Open team info'
                  onClick={() => setIsTeamOrientationOpen(true)}
                >
                  <Info className='h-3.5 w-3.5' />
                </Button>
              ) : undefined
            }
          />
        }
        content={
          <OGESidebarContent
            openEvents={openEvents}
            onEventToggle={onEventToggle}
            openRounds={openRounds}
            onRoundToggle={onRoundToggle}
          />
        }
        chatContent={
          <Tabbar
            isDecision={isDecisionPhase}
            isManualMode={manualMode}
            embedded
          />
        }
      />

      <div className='flex flex-col w-full h-full overflow-auto'>
        <div className='sticky top-0 z-10 px-14 py-2 ml-[2.5rem]'>
          <GamePhaseInfo />
        </div>

        <div className='flex-1 min-h-0 flex flex-col'>
          <ChatRoom
            showNavbar={true}
            showAdvisors={showAdvisors}
            showNegotiationPill={showNegotiationPill}
            showAdvisorPill={showAdvisorPill}
            showNegotiation={showNegotiation}
            showDecisions={showDecisions}
            phaseId={phaseId || 0}
            isWhiteCell={isWhiteCell}
          />
        </div>
        {(roundStatus === ActivityState.Completed || gameEnded) &&
          user?.role !== UserRole.Professor && (
            <div className='fixed bottom-0 left-[320px] right-0 flex justify-center py-4 overflow-x-hidden'>
              <div className='w-full max-w-4xl mx-auto px-4'>
                <Alert className='bg-amber-50 border-amber-300 text-amber-800'>
                  <div className='flex items-center gap-2 mb-1'>
                    <img
                      src='/icons/caution.svg'
                      alt='Info'
                      className='text-amber-500'
                    />
                    <AlertTitle className='text-lg font-bold'>
                      {gameEnded ||
                      (roundStatus === ActivityState.Completed &&
                        totalRounds === currentRound)
                        ? 'Game Over'
                        : 'Round Over'}
                    </AlertTitle>
                  </div>
                  <AlertDescription className='flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center flex-wrap'>
                    <span className='text-amber-700'>
                      {gameEnded ||
                      (roundStatus === ActivityState.Completed &&
                        totalRounds === currentRound)
                        ? 'The game has ended.'
                        : "Round time has ended. If you haven't submitted the decision, do submit it but keep in mind it'll be marked as late."}
                    </span>
                    {(gameEnded ||
                      (roundStatus === ActivityState.Completed &&
                        totalRounds === currentRound)) && (
                      <Link
                        to='/player-dashboard'
                        className='flex-shrink-0 px-4 py-2 font-medium text-center transition-colors bg-blue-600 rounded-md hover:bg-blue-700 whitespace-nowrap'
                      >
                        Return to Dashboard
                      </Link>
                    )}
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          )}
      </div>
      <TeamOrientationInfoDialog
        open={isTeamOrientationOpen}
        onOpenChange={setIsTeamOrientationOpen}
        onAcknowledge={handleOrientationAcknowledge}
        team={currentTeam}
        members={teamMembers}
        currentUserEmail={user?.email}
      />
    </SidebarProvider>
  )
}

export default PlayerGame
